import React, { useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Avatar,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  useGetRoleDefinitionsQuery,
  useGetUsersQuery,
  useGetUserRoleHistoryQuery,
  useAssignUserRoleMutation,
  AdminRole,
  AdminUser,
  RoleDefinition,
  PermissionMatrixRow,
  AuditLogEntry,
} from '@store/api/adminApi'
import { useAppSelector } from '@store/hooks'
import AdminPageGate from '@components/auth/AdminPageGate'

const ROLE_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error' | 'success'> = {
  super_admin: 'error',
  admin: 'warning',
  moderator: 'info',
  user: 'default',
}

const RoleHistoryDialog: React.FC<{ user: AdminUser; open: boolean; onClose: () => void }> = ({
  user,
  open,
  onClose,
}) => {
  const { data, isLoading } = useGetUserRoleHistoryQuery(user.id, { skip: !open })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          Role History — {user.username}
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : !data?.data?.length ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>No role changes recorded.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Changed By</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.data.map((entry: AuditLogEntry) => {
                  const before = entry.beforeState?.role ?? '—'
                  const after = entry.afterState?.role ?? '—'
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Typography variant="caption">
                          {format(new Date(entry.timestamp), 'MMM dd yyyy HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.admin?.email ?? entry.adminId ?? 'system'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={before} size="small" color={ROLE_COLORS[before] ?? 'default'} />
                      </TableCell>
                      <TableCell>
                        <Chip label={after} size="small" color={ROLE_COLORS[after] ?? 'default'} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const AssignRoleDialog: React.FC<{
  user: AdminUser | null
  roleDefinitions: RoleDefinition[]
  actorRole: string
  open: boolean
  onClose: () => void
}> = ({ user, roleDefinitions, actorRole, open, onClose }) => {
  const [role, setRole] = useState<AdminRole>(user?.role as AdminRole ?? 'user')
  const [expiresAt, setExpiresAt] = useState('')
  const [adminNotes, setAdminNotes] = useState('')
  const [error, setError] = useState('')

  const [assignRole, { isLoading }] = useAssignUserRoleMutation()

  const actorLevel = roleDefinitions.find((d) => d.role === actorRole)?.level ?? 0
  const assignableRoles = roleDefinitions.filter(
    (def) => def.role !== 'user' && def.level < actorLevel,
  )

  React.useEffect(() => {
    if (user) {
      setRole(user.role as AdminRole)
      setExpiresAt('')
      setAdminNotes('')
      setError('')
    }
  }, [user])

  const handleSave = async () => {
    if (!user) return
    try {
      await assignRole({
        userId: user.id,
        role,
        roleExpiresAt: expiresAt || null,
        adminNotes: adminNotes || undefined,
      }).unwrap()
      onClose()
    } catch (err: any) {
      setError(err?.data?.error?.message ?? 'Failed to assign role')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AdminIcon />
          Assign Role — {user?.username}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value as AdminRole)}>
              {assignableRoles.map((def) => (
                <MenuItem key={def.role} value={def.role}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip label={def.role} size="small" color={ROLE_COLORS[def.role] ?? 'default'} />
                    <Typography variant="body2" color="text.secondary">{def.description}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Role Expires At (optional)"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty for permanent assignment"
          />
          <TextField
            fullWidth
            label="Admin Notes"
            multiline
            rows={2}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving…' : 'Assign Role'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const AdminRoleManagement: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth)
  const [historyUser, setHistoryUser] = useState<AdminUser | null>(null)
  const [assignUser, setAssignUser] = useState<AdminUser | null>(null)
  const [userSearch, setUserSearch] = useState('')

  const { data: roleData, isLoading: rolesLoading } = useGetRoleDefinitionsQuery()
  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery({
    page: 1,
    limit: 50,
    ...(userSearch && { search: userSearch }),
  })

  const actorRole = (user as { role?: string })?.role
  const denied = actorRole !== 'admin' && actorRole !== 'super_admin'

  const roleDefinitions = roleData?.data?.roles ?? []
  const permissionMatrix: PermissionMatrixRow[] = roleData?.data?.matrix ?? []

  return (
    <AdminPageGate permission="users:read" title="Role Management">
    {denied ? (
      <Box>
        <Typography variant="h4" gutterBottom>Role Management</Typography>
        <Alert severity="error">Admin access required.</Alert>
      </Box>
    ) : (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <SecurityIcon color="primary" />
        <Typography variant="h4" component="h1">Role Management</Typography>
      </Box>

      {/* Role Definitions */}
      <Typography variant="h6" gutterBottom>Role Definitions & Permissions</Typography>
      {rolesLoading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={1} mb={4}>
          {roleDefinitions.map((def) => (
            <Accordion key={def.role} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip label={def.role} color={ROLE_COLORS[def.role] ?? 'default'} size="small" />
                  <Typography variant="body2" color="text.secondary">{def.description}</Typography>
                  <Chip label={`Level ${def.level}`} size="small" variant="outlined" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {def.permissions.length === 0 || def.permissions[0] === '*' ? (
                  <Typography variant="body2" color="text.secondary">
                    {def.permissions[0] === '*' ? 'All permissions (unrestricted)' : 'No special permissions'}
                  </Typography>
                ) : (
                  <Grid container spacing={1}>
                    {def.permissions.map((perm) => (
                      <Grid item key={perm}>
                        <Chip label={perm} size="small" variant="outlined" color="primary" />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {permissionMatrix.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Permission matrix
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <code>super_admin</code> has all permissions via wildcard (<code>*</code>) and is omitted from this table.
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Permission</TableCell>
                  <TableCell>admin</TableCell>
                  <TableCell>moderator</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissionMatrix.map((row) => (
                  <TableRow key={row.permission}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {row.permission}
                      </Typography>
                    </TableCell>
                    {(['admin', 'moderator'] as const).map((role) => (
                      <TableCell key={role}>
                        {row.roles.includes(role) ? (
                          <Chip label="yes" size="small" color="success" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* User Role Assignment */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">User Role Assignments</Typography>
        <TextField
          size="small"
          placeholder="Search users…"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          sx={{ width: 240 }}
        />
      </Box>

      {usersLoading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Current Role</TableCell>
                <TableCell>Role Expires</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersData?.data?.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {u.firstName?.[0] ?? u.email[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={u.role} color={ROLE_COLORS[u.role] ?? 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {u.roleExpiresAt
                        ? format(new Date(u.roleExpiresAt), 'MMM dd yyyy')
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.isActive ? 'Active' : 'Inactive'}
                      color={u.isActive ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        startIcon={<HistoryIcon />}
                        onClick={() => setHistoryUser(u)}
                      >
                        History
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AdminIcon />}
                        onClick={() => setAssignUser(u)}
                      >
                        Assign
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Role History Dialog */}
      {historyUser && (
        <RoleHistoryDialog
          user={historyUser}
          open={!!historyUser}
          onClose={() => setHistoryUser(null)}
        />
      )}

      {/* Assign Role Dialog */}
      <AssignRoleDialog
        user={assignUser}
        roleDefinitions={roleDefinitions}
        actorRole={actorRole ?? 'user'}
        open={!!assignUser}
        onClose={() => setAssignUser(null)}
      />

      {/* Anti-escalation notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Anti-escalation enforcement:</strong> You can only assign roles with a lower level than your own.
          Role assignments are fully audited and logged.
        </Typography>
      </Alert>
    </Box>
    )}
    </AdminPageGate>
  )
}

export default AdminRoleManagement
